<template>
    <div>
        <div v-for="(landAccess, key) in accessLands">
            <AccessLandControl
                :value="landAccess"
                :key="key"
                :land-types="landTypes"
                :validate="validate"
                @input="accessLands[key] = $event"
                @remove="removeAccessLand(key)"/>
        </div>

        <div class="mt-3">
            <button class="btn btn-outline-primary w-auto" @click="addAccessLand()">
                Přidat přístup na pozemek
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import AccessLandControl from '@/components/estimates/common/controls/AccessLandControl.vue';
import {AccessLand} from '@/components/estimates/common/controls/values';
import {Control} from '@/composables/Control';
import {highestKey} from '@/utils';
import {fromPairs} from 'lodash-es';
import {defineComponent, onMounted, PropType, ref, watch} from 'vue';

interface Values { [key: number]: (AccessLand | null); }

export default defineComponent({
    components: {AccessLandControl},
    props: {
        value: {
            type: Array as PropType<AccessLand[] | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        landTypes: {
            type: Array as PropType<string[]>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const accessLands = ref<Values>({});

        const {updateValue, isInvalid} = Control<AccessLand[]>(props, emit, {
            getValue: () => Object.values(accessLands.value).filter(r => r !== null),
            validateCurrentValue: () => Object.values(accessLands.value).every(r => r !== null),
        });

        onMounted(() => {
            const value = props.value;

            if (value !== null) {
                accessLands.value = fromPairs([...value.entries()]);
            }

            updateValue();
        });

        watch(accessLands, () => {
            updateValue();
        }, {deep: true});

        function addAccessLand(): void {
            const landAccesses = Object.assign({}, accessLands.value);

            landAccesses[highestKey(landAccesses) + 1] = null;

            accessLands.value = landAccesses;
        }

        function removeAccessLand(key: number): void {
            const landAccesses = Object.assign({}, accessLands.value);

            delete landAccesses[key];

            accessLands.value = landAccesses;
        }

        return {
            accessLands,
            addAccessLand,
            removeAccessLand,
            isInvalid,
        };
    },
});
</script>
